import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmailCheckForm } from '../components/EmailCheckForm';
import { LoginForm } from '../components/LoginForm';
import { RegisterForm } from '../components/RegisterForm';
import { useApp } from '../context/AppContext';
import { authService } from '../services/authService';
import { getDashboardRoute } from '../utils/navigation';
import { featureFlagsService } from '../services/featureFlagsService';

type AuthStep = 'email-check' | 'login' | 'register';

export const AuthPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<AuthStep>('email-check');
  const [email, setEmail] = useState('');
  const [createAccountEnabled, setCreateAccountEnabled] = useState(true);
  const [isLoadingFeatureFlags, setIsLoadingFeatureFlags] = useState(true);
  const navigate = useNavigate();
  const { state, dispatch } = useApp();

  // Check if user is already authenticated
  useEffect(() => {
    const loadFeatureFlags = async () => {
      setIsLoadingFeatureFlags(true);
      const flags = await featureFlagsService.getFeatureFlags();
      setCreateAccountEnabled(flags.create_account_enabled);
      setIsLoadingFeatureFlags(false);
    };

    loadFeatureFlags();
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      if (authService.isAuthenticated() && state.user) {
        // Redirect to intended destination or appropriate dashboard
        const redirectTo = state.redirectUrl || getDashboardRoute(state.user);
        dispatch({ type: 'SET_REDIRECT_URL', payload: null }); // Clear redirect URL
        navigate(redirectTo);
        return;
      }

      // Try to get user profile if token exists
      if (authService.isAuthenticated()) {
        try {
          const response = await authService.getProfile();
          dispatch({
            type: 'SET_USER',
            payload: {
              id: response.data.id,
              email: response.data.email,
              displayName: `${response.data.firstName} ${response.data.lastName}`,
              firstName: response.data.firstName,
              lastName: response.data.lastName,
              role: response.data.role,
            },
          });
          // Redirect to intended destination or appropriate dashboard
          const redirectTo = state.redirectUrl || getDashboardRoute(response.data);
          dispatch({ type: 'SET_REDIRECT_URL', payload: null }); // Clear redirect URL
          navigate(redirectTo);
        } catch (error) {
          // Token is invalid, remove it
          authService.removeToken();
        }
      }
    };

    checkAuth();
  }, [navigate, dispatch, state.user, state.redirectUrl]);

  const handleEmailExists = (userEmail: string) => {
    setEmail(userEmail);
    setCurrentStep('login');
  };

  const handleEmailNew = (userEmail: string) => {
    setEmail(userEmail);
    setCurrentStep('register');
  };

  const handleBackToEmailCheck = () => {
    setCurrentStep('email-check');
    setEmail('');
  };

  const handleAuthSuccess = () => {
    // Clear redirect URL since navigation is handled by the form components
    dispatch({ type: 'SET_REDIRECT_URL', payload: null });
  };

  // Don't render anything while checking authentication
  if (authService.isAuthenticated() && state.user) {
    return null;
  }

  return (
    <div style={{ position: 'relative' }}>
      {/* Email Check Form - always rendered but conditionally visible */}
      <div style={{ 
        display: currentStep === 'email-check' ? 'block' : 'none',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: currentStep === 'email-check' ? 1 : -1
      }}>
        <EmailCheckForm
          onEmailExists={handleEmailExists}
          onEmailNew={handleEmailNew}
          createAccountEnabled={createAccountEnabled}
          isLoadingFeatureFlags={isLoadingFeatureFlags}
        />
      </div>

      {/* Login Form - always rendered but conditionally visible */}
      <div style={{ 
        display: currentStep === 'login' ? 'block' : 'none',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: currentStep === 'login' ? 1 : -1
      }}>
        <LoginForm
          email={email}
          onBack={handleBackToEmailCheck}
          onSuccess={handleAuthSuccess}
        />
      </div>

      {/* Register Form - always rendered but conditionally visible */}
      <div style={{ 
        display: currentStep === 'register' ? 'block' : 'none',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: currentStep === 'register' ? 1 : -1
      }}>
        <RegisterForm
          email={email}
          onBack={handleBackToEmailCheck}
          onSuccess={handleAuthSuccess}
        />
      </div>
    </div>
  );
};
